<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { fetchAgreementAdmin, saveAgreementAdmin } from '@/api/modules/agreementsAdmin'
import HtmlRichEditor from '@/components/HtmlRichEditor.vue'

const props = withDefaults(
  defineProps<{
    agreementId: number
    pageTitle: string
    hint: string
    contentLabel?: string
  }>(),
  { contentLabel: '协议 HTML' },
)

const loading = ref(false)
const saving = ref(false)
const content = ref('')

async function reload() {
  loading.value = true
  try {
    const { data } = await fetchAgreementAdmin(props.agreementId)
    content.value = data.content ?? ''
  } finally {
    loading.value = false
  }
}

async function submit() {
  saving.value = true
  try {
    const { data } = await saveAgreementAdmin(props.agreementId, content.value)
    if (Number(data.status) === 1) {
      ElMessage.success(data.msg ?? 'Saved')
      await reload()
    } else {
      ElMessage.warning(String(data.msg || 'Validation failed'))
    }
  } finally {
    saving.value = false
  }
}

onMounted(reload)

watch(
  () => props.agreementId,
  () => reload(),
)
</script>

<template>
  <PageChrome
    :title="pageTitle"
    :description="`${hint} · 前台匿名读取 GET /api/portal/public/agreements/${agreementId} · 下方为可视化富文本编辑器`"
  >
    <template #actions>
      <el-button :loading="loading" @click="reload">Reload</el-button>
      <el-button type="primary" :loading="saving" @click="submit">Submit</el-button>
    </template>

    <el-alert type="info" show-icon :closable="false" style="margin-bottom: 18px">
      表 <code>xieyi</code> · 行 <code>id={{ agreementId }}</code>
    </el-alert>

    <el-card shadow="hover">
      <template #header>
        <span class="agr-head">{{ contentLabel }}</span>
      </template>
      <!-- 不要用 v-if 配合 loading 销毁编辑器：wangEditor 二次挂载易整页空白（无工具栏） -->
      <div
        class="agr-editor-shell"
        v-loading="loading"
        element-loading-text="加载协议内容…"
      >
        <HtmlRichEditor
          :key="agreementId"
          v-model="content"
          editor-min-height="480px"
          :disabled="saving || loading"
        />
      </div>
    </el-card>
  </PageChrome>
</template>

<style scoped lang="scss">
.agr-head {
  font-weight: 600;
}

.agr-editor-shell {
  position: relative;
  min-height: 480px;
}
</style>
